// client/src/neetjee_guru/components/libraryManagement/LibraryDashboard.jsx
import React, { Component } from "react";
import { Row, Col, Card, Table, Badge, Pagination, Form } from "react-bootstrap";
import * as Cookies from "es-cookie";
import { GET_LIBRARY_DASHBOARD, GET_LIBRARY_REPORT } from "./queries";

class LibraryDashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      stats: {
        total_books: 0,
        total_copies: 0,
        active_loans: 0,
        overdue_loans: 0,
        fine_collected_today: 0.0,
        pending_fines: 0.0,
      },
      recentLoans: [],
      loading: true,
      error: null,
      
      // Pagination State
      currentPage: 1,
      pageSize: 10,
    };
  }

  componentDidMount() {
    this.fetchDashboardData();
  }

  handlePageChange = (page) => {
    this.setState({ currentPage: page });
  };

  handlePageSizeChange = (e) => {
    const size = parseInt(e.target.value) || 10;
    this.setState({ pageSize: size, currentPage: 1 });
  };

  fetchDashboardData = async () => {
    const { client } = this.props;
    const instId = parseInt(Cookies.get("institutionid") || Cookies.get("institute_id") || "0");
    const branchId = parseInt(Cookies.get("branch_id") || "0");

    try {
      this.setState({ loading: true });
      
      const statsRes = await client.query({
        query: GET_LIBRARY_DASHBOARD,
        variables: { institute_id: instId, branch_id: branchId },
        fetchPolicy: "network-only",
      });

      const reportRes = await client.query({
        query: GET_LIBRARY_REPORT,
        variables: { type: "active_loans", institute_id: instId, branch_id: branchId },
        fetchPolicy: "network-only",
      });

      this.setState({
        stats: statsRes.data.getLibraryDashboard,
        recentLoans: reportRes.data.getLibraryReport || [],
        loading: false,
        error: null,
        currentPage: 1,
      });
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      this.setState({ error: err.message, loading: false });
    }
  };

  formatCurrency = (val) =>
    "₹" + parseFloat(val || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  renderShimmer = () => {
    return (
      <div>
        <Row className="mb-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Col lg={2} md={4} sm={6} key={i} className="mb-3">
              <div className="shimmer" style={{ height: 100, borderRadius: 12 }} />
            </Col>
          ))}
        </Row>
        <div className="shimmer" style={{ height: 300, borderRadius: 12 }} />
      </div>
    );
  };

  render() {
    const { stats, recentLoans, loading, error, currentPage, pageSize } = this.state;

    const totalCount = recentLoans.length;
    const totalPages = Math.ceil(totalCount / pageSize) || 1;
    const paginatedLoans = recentLoans.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const pageItems = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    for (let p = startPage; p <= endPage; p++) {
      pageItems.push(
        <Pagination.Item 
          key={p} 
          active={p === currentPage} 
          onClick={() => this.handlePageChange(p)}
        >
          {p}
        </Pagination.Item>
      );
    }

    if (loading) return this.renderShimmer();

    if (error) {
      return (
        <div className="p-5 text-center">
          <h4 className="text-danger">Failed to load dashboard data</h4>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={this.fetchDashboardData}>
            Retry
          </button>
        </div>
      );
    }

    const cards = [
      { label: "Total Books", value: stats.total_books, color: "#3b82f6", icon: "fal fa-books", sub: "Unique titles" },
      { label: "Total Copies", value: stats.total_copies, color: "#10b981", icon: "fal fa-copy", sub: "Total physical stock" },
      { label: "Active Checkouts", value: stats.active_loans, color: "#6366f1", icon: "fal fa-book-reader", sub: "Currently checked out" },
      { label: "Overdue Issues", value: stats.overdue_loans, color: "#ef4444", icon: "fal fa-exclamation-circle", sub: "Pending return" },
      { label: "Fine Collected Today", value: this.formatCurrency(stats.fine_collected_today), color: "#059669", icon: "fal fa-cash-register", sub: "Collections" },
      { label: "Pending Fines", value: this.formatCurrency(stats.pending_fines), color: "#d97706", icon: "fal fa-hand-holding-usd", sub: "Unpaid dues" },
    ];

    return (
      <div>
        {/* KPI Cards row */}
        <Row className="g-3 mb-4">
          {cards.map((card) => (
            <Col lg={2} md={4} sm={6} xs={12} key={card.label}>
              <div
                style={{
                  background: "#fff",
                  border: "1px solid #e5e7eb",
                  borderTop: `4px solid ${card.color}`,
                  borderRadius: 12,
                  padding: "16px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
                  height: "100%",
                }}
              >
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#6b7280" }}>{card.label}</span>
                  <i className={card.icon} style={{ color: card.color, fontSize: 16 }} />
                </div>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#1f2937" }}>{card.value}</div>
                <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 4 }}>{card.sub}</div>
              </div>
            </Col>
          ))}
        </Row>

        {/* Recent Loans table */}
        <Card className="custom-card-shadow border-0" style={{ borderRadius: 12 }}>
          <Card.Header
            style={{
              background: "#f8fafc",
              borderBottom: "1px solid #e2e8f0",
              fontWeight: 800,
              fontSize: 15,
              color: "#1f2937",
              padding: "16px 20px",
              borderRadius: "12px 12px 0 0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>
              <i className="fal fa-list-alt mr-2" style={{ color: "#6366f1" }} />
              Active Books Issued
            </span>
            <Badge variant="primary" style={{ background: "#e0e7ff", color: "#4f46e5", padding: "6px 12px", borderRadius: 20 }}>
              {recentLoans.length} Active Issues
            </Badge>
          </Card.Header>
          <Card.Body className="p-0">
            {paginatedLoans.length === 0 ? (
              <div className="text-center p-5 text-muted">
                <i className="fal fa-books fa-3x d-block mb-3 text-light" />
                <p>No active issues found at the moment.</p>
              </div>
            ) : (
              <div>
                <Table responsive className="mb-0 align-middle" hover style={{ fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "#f8fafc", color: "#475569" }}>
                      <th style={{ padding: "12px 20px" }}>#</th>
                      <th>Student Name</th>
                      <th>Book Details</th>
                      <th>Issue Date</th>
                      <th>Issue Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedLoans.map((loan, idx) => (
                      <tr key={loan.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                        <td style={{ padding: "12px 20px", color: "#64748b" }}>{(currentPage - 1) * pageSize + idx + 1}</td>
                        <td style={{ fontWeight: 700, color: "#1f2937" }}>{loan.student_name}</td>
                        <td style={{ color: "#475569" }}>{loan.description}</td>
                        <td>{loan.date}</td>
                        <td>
                          <Badge
                            style={{
                              background: loan.description.includes("Overdue") ? "#fee2e2" : "#dcfce7",
                              color: loan.description.includes("Overdue") ? "#ef4444" : "#15803d",
                              padding: "4px 8px",
                              borderRadius: 12,
                            }}
                          >
                            {loan.description.includes("Overdue") ? "Overdue" : "Active"}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                {/* Pagination Controls Dock */}
                <div className="d-flex justify-content-between align-items-center px-4 py-3 bg-light border-top" style={{ borderBottomLeftRadius: 12, borderBottomRightRadius: 12 }}>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Showing <strong>{totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1}</strong> to <strong>{Math.min(currentPage * pageSize, totalCount)}</strong> of <strong>{totalCount}</strong> entries
                  </div>
                  
                  <div className="d-flex align-items-center" style={{ gap: 16 }}>
                    <Form.Control 
                      as="select" 
                      size="sm" 
                      value={pageSize} 
                      onChange={this.handlePageSizeChange} 
                      style={{ width: 100, fontSize: 12, height: 32, borderRadius: 6, border: "1.5px solid #e2e8f0" }}
                    >
                      <option value={10}>10 / page</option>
                      <option value={25}>25 / page</option>
                      <option value={50}>50 / page</option>
                      <option value={100}>100 / page</option>
                    </Form.Control>

                    <Pagination size="sm" className="mb-0">
                      <Pagination.First disabled={currentPage === 1} onClick={() => this.handlePageChange(1)} />
                      <Pagination.Prev disabled={currentPage === 1} onClick={() => this.handlePageChange(currentPage - 1)} />
                      {pageItems}
                      <Pagination.Next disabled={currentPage >= totalPages} onClick={() => this.handlePageChange(currentPage + 1)} />
                      <Pagination.Last disabled={currentPage >= totalPages} onClick={() => this.handlePageChange(totalPages)} />
                    </Pagination>
                  </div>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
    );
  }
}

export default LibraryDashboard;
